// Invoice view page — displays a single invoice with action buttons

import { useTranslation } from "react-i18next";
import { Button } from "@cieda/djayma-ui";
import type { Invoice } from "@/lib/invoicing/types";
import { InvoiceStatus } from "@/lib/invoicing/types";
import { InvoiceTemplate } from "./InvoiceTemplate";

interface InvoiceViewProps {
  readonly invoice: Invoice;
  readonly onDownloadPdf?: () => void;
  readonly onSendEmail?: () => void;
}

const STATUS_STYLES: Record<InvoiceStatus, string> = {
  [InvoiceStatus.DRAFT]: "bg-stone-100 text-stone-700",
  [InvoiceStatus.ISSUED]: "bg-amber-50 text-amber-700 border border-amber-200",
  [InvoiceStatus.PAID]: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  [InvoiceStatus.CANCELLED]: "bg-red-50 text-red-700 border border-red-200",
  [InvoiceStatus.REFUNDED]: "bg-violet-50 text-violet-700 border border-violet-200",
};

function handlePrint(): void {
  window.print();
}

export function InvoiceView({
  invoice,
  onDownloadPdf,
  onSendEmail,
}: InvoiceViewProps) {
  const { t } = useTranslation("invoicing");

  const statusKey = invoice.status.toLowerCase() as Lowercase<InvoiceStatus>;

  return (
    <div className="mx-auto max-w-[900px] space-y-6 px-4 py-8">
      {/* ---------------------------------------------------------------- */}
      {/* Top bar: status badge + actions (hidden in print)                 */}
      {/* ---------------------------------------------------------------- */}
      <div className="no-print flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <h1 className="font-['Cormorant_Garamond',serif] text-2xl font-bold text-stone-900">
            {t("title")} {invoice.number}
          </h1>
          <span
            className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium ${STATUS_STYLES[invoice.status]}`}
          >
            {t(`status.${statusKey}`)}
          </span>
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onDownloadPdf ?? handlePrint}
          >
            <DownloadIcon />
            {t("actions.download_pdf")}
          </Button>
          <Button variant="outline" size="sm" onClick={handlePrint}>
            <PrintIcon />
            {t("actions.print")}
          </Button>
          <Button variant="outline" size="sm" onClick={onSendEmail}>
            <MailIcon />
            {t("actions.send_email")}
          </Button>
        </div>
      </div>

      {/* ---------------------------------------------------------------- */}
      {/* Invoice body                                                      */}
      {/* ---------------------------------------------------------------- */}
      <div className="rounded-xl border border-stone-200 bg-white shadow-sm print:border-0 print:shadow-none">
        <InvoiceTemplate invoice={invoice} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline SVG icons (avoids extra dependencies)
// ---------------------------------------------------------------------------

function DownloadIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function PrintIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="6 9 6 2 18 2 18 9" />
      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
      <rect x="6" y="14" width="12" height="8" />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  );
}
